import {fromSeconds} from 'common/date';
import * as React from 'react';
import {
  buildLotRows,
  formatCents,
  issuerCurrency,
  type EquityIssuerData,
  type LotRow,
} from 'src/EquityView/equitySeries';
import {cn} from 'src/lib/utils';

const shares = (value: number): string => value.toLocaleString('en-US');

const dateLabel = (seconds: number): string =>
  fromSeconds(seconds).toLocaleString({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const SOURCE_LABELS: Record<string, string> = {
  rsu_release: 'RSU release',
  exercise: 'Exercise',
  purchase: 'Purchase',
  other: 'Other',
};

/**
 * Per-lot holdings, oldest first. Held lots by default; the toggle reveals
 * the fully sold history too (dimmed). Value and unrealized gain are at the
 * latest mark; the footer totals only what is still held.
 */
export const LotsTable = ({
  issuer,
  todaySeconds,
}: {
  issuer: EquityIssuerData;
  todaySeconds: number;
}) => {
  const [showSoldOut, setShowSoldOut] = React.useState(false);
  const currency = issuerCurrency(issuer);
  const cents = (value: number): string => formatCents(value, currency);
  const allRows = React.useMemo(
    () => buildLotRows(issuer, todaySeconds),
    [issuer, todaySeconds],
  );
  const heldRows = allRows.filter((row) => row.sharesRemaining > 0);
  const rows = showSoldOut ? allRows : heldRows;
  const soldOutCount = allRows.length - heldRows.length;

  const totals = heldRows.reduce(
    (sum, row) => ({
      remaining: sum.remaining + row.sharesRemaining,
      basisCents: sum.basisCents + row.basisCents,
      valueCents:
        row.valueCents == null || sum.valueCents == null
          ? null
          : sum.valueCents + row.valueCents,
      unrealizedCents:
        row.unrealizedCents == null || sum.unrealizedCents == null
          ? null
          : sum.unrealizedCents + row.unrealizedCents,
    }),
    {
      remaining: 0,
      basisCents: 0,
      valueCents: 0 as number | null,
      unrealizedCents: 0 as number | null,
    },
  );

  // buildLotRows omits future-dated lots; those are persisted records, so
  // say so rather than letting them look missing (same rule as events).
  const futureCount = issuer.lots.length - allRows.length;

  if (allRows.length === 0) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        {futureCount > 0
          ? `No lots yet — ${futureCount} future-dated lot${
              futureCount === 1 ? '' : 's'
            } not shown.`
          : 'No lots recorded yet.'}
      </div>
    );
  }

  return (
    <section>
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-foreground text-base font-semibold">Lots</h2>
        {futureCount > 0 && (
          <span className="text-muted-foreground text-xs">
            {futureCount} future-dated not shown
          </span>
        )}
        {soldOutCount > 0 && (
          <button
            type="button"
            onClick={() => setShowSoldOut((value) => !value)}
            className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
          >
            {showSoldOut
              ? 'Hide fully sold lots'
              : `Show ${soldOutCount} fully sold lot${soldOutCount === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-left text-xs">
              <th className="py-1 pr-2 font-normal">Acquired</th>
              <th className="py-1 pr-2 font-normal">Source</th>
              <th className="py-1 pr-2 text-right font-normal">Acquired sh</th>
              <th className="py-1 pr-2 text-right font-normal">Held sh</th>
              <th className="py-1 pr-2 text-right font-normal">Basis /sh</th>
              <th className="py-1 pr-2 text-right font-normal">Basis</th>
              <th className="py-1 pr-2 text-right font-normal">Value</th>
              <th className="py-1 pr-2 text-right font-normal">Unrealized</th>
              <th className="py-1 font-normal">Term</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: LotRow) => {
              const soldOut = row.sharesRemaining === 0;
              return (
                <tr
                  key={row.lot._id}
                  className={cn(
                    'border-border border-t',
                    soldOut && 'text-muted-foreground',
                  )}
                >
                  <td className="py-1.5 pr-2 whitespace-nowrap">
                    {dateLabel(row.lot.acquiredDate)}
                  </td>
                  <td className="text-muted-foreground py-1.5 pr-2 whitespace-nowrap">
                    {SOURCE_LABELS[row.lot.source] ?? row.lot.source}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {shares(row.lot.sharesAcquired)}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-medium tabular-nums">
                    {shares(row.sharesRemaining)}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {row.basis.format()}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {soldOut ? '—' : cents(row.basisCents)}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {soldOut || row.valueCents == null
                      ? '—'
                      : cents(row.valueCents)}
                  </td>
                  <td
                    className={cn(
                      'py-1.5 pr-2 text-right tabular-nums',
                      !soldOut &&
                        row.unrealizedCents != null &&
                        (row.unrealizedCents >= 0
                          ? 'text-positive'
                          : 'text-destructive'),
                    )}
                  >
                    {soldOut || row.unrealizedCents == null
                      ? '—'
                      : cents(row.unrealizedCents)}
                  </td>
                  <td className="py-1.5">
                    {soldOut ? (
                      <span className="text-muted-foreground text-xs">
                        sold
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                          row.longTerm
                            ? 'border-border text-muted-foreground'
                            : 'border-destructive/40 text-destructive',
                        )}
                      >
                        {row.longTerm ? 'LT' : 'ST'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-border border-t-2 font-medium">
              <td className="py-1.5 pr-2" colSpan={3}>
                Held total
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {shares(totals.remaining)}
              </td>
              <td className="py-1.5 pr-2" />
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {cents(totals.basisCents)}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {totals.valueCents == null ? '—' : cents(totals.valueCents)}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {totals.unrealizedCents == null
                  ? '—'
                  : cents(totals.unrealizedCents)}
              </td>
              <td className="py-1.5" />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};
